import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Pagination,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  Slideshow as SlideshowIcon,
  Description as GenericFileIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  fetchIngestedFileListing,
  getIngestionProgressLog,
  deleteDocument,
} from "../../services/apiService";
import FileProcessingProgress from "./FileProcessingProgress";
import Loader from "../Loader";
import SummaryDialog from "../SummaryDialog";

/**
 * Component for displaying a list of available files
 */
const FilesList = ({ onSelectFile, onDeleteFile, selectedFiles = [] }) => {
  const [files, setFiles] = useState([]);
  const [filesData, setFilesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingFile, setProcessingFile] = useState(null);
  const [processingStatus, setProcessingStatus] = useState({});
  const [processingDialogOpen, setProcessingDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filesPerPage] = useState(10);
  const [paginatedFiles, setPaginatedFiles] = useState([]);

  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredFiles, setFilteredFiles] = useState([]);

  // Add new state to control search field visibility
  const [searchVisible, setSearchVisible] = useState(false);

  // Toggle search field visibility
  const toggleSearch = () => {
    setSearchVisible((prev) => !prev);
    // Clear search when hiding the search field
    if (searchVisible) {
      setSearchQuery("");
    }
  };

  // Focus the search input when it becomes visible
  const searchInputRef = useRef(null);
  useEffect(() => {
    if (searchVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchVisible]);

  // Helper function to determine file status based on server status
  const determineStatusFromServer = useCallback((file) => {
    if (!file?.status || file.status === "") {
      return "unknown";
    }

    if (file.status === "completed") {
      return "completed";
    }

    if (file.status.includes("started") || file.status.includes("in_progress")) {
      return hasProcessingTimedOut(file.start_time) ? "failed" : "in_progress";
    }

    if (file.status.includes("failed") || file.status.includes("error")) {
      return "failed";
    }

    return "unknown";
  }, []);

  // Helper function to determine status based on process steps
  const determineStatusFromSteps = useCallback((file) => {
    if (!file.process_steps ||
        typeof file.process_steps.completed === "undefined" ||
        typeof file.process_steps.total === "undefined") {
      return null; // No step information available
    }

    const { completed, total } = file.process_steps;
    const hasTimedOut = hasProcessingTimedOut(file.start_time);

    // If timed out and not fully completed, mark as failed
    if (hasTimedOut && completed < total) {
      return "failed";
    }

    // If all steps completed, mark as completed
    if (completed === total && total > 0) {
      return "completed";
    }

    // If steps are in progress
    if (completed < total) {
      return "in_progress";
    }

    return null;
  }, []);

  // Helper function to determine overall progress status for a file
  const determineFileProgressStatus = useCallback((file) => {
    // First try to determine status from process steps
    const stepStatus = determineStatusFromSteps(file);
    if (stepStatus) {
      return stepStatus;
    }

    // Fall back to server status
    return determineStatusFromServer(file);
  }, [determineStatusFromServer, determineStatusFromSteps]);

  // Helper function to process file data and add progress status
  const processFileData = useCallback((responseData) => {
    return responseData.map((file) => ({
      ...file,
      progressStatus: determineFileProgressStatus(file)
    }));
  }, [determineFileProgressStatus]);

  // Helper function to handle response data processing
  const handleResponseData = useCallback((response) => {
    // console.log("[FilesList] Processing response data:", response);
    // console.log("response.data", response.data)
    if (response.data && Array.isArray(response.data)) {
      const fileList = response.data.map((item) => item.filename);
      const total = Math.ceil(fileList.length / filesPerPage);
      
      // console.log("[FilesList] Found files in data array:", fileList);
      setFiles(fileList);
      setTotalPages(total || 1);
      setFilesData(processFileData(response.data));
      
      // Ensure filteredFiles is immediately updated if no search query
      if (!searchQuery.trim()) {
        setFilteredFiles(fileList);
      }
    } else {
      // Fall back to legacy format
      const fileList = response.filenames || [];
      // console.log("[FilesList] Using legacy format, files:", fileList);
      setFiles(fileList);
      setTotalPages(Math.ceil(fileList.length / filesPerPage) || 1);
      
      // Ensure filteredFiles is immediately updated if no search query
      if (!searchQuery.trim()) {
        setFilteredFiles(fileList);
      }
    }
  }, [filesPerPage, processFileData, searchQuery]);

  // Load files on component mount and set up polling
  useEffect(() => {
    const loadFiles = async (showLoader = false) => {
      if (showLoader) {
        setLoading(true);
      }

      try {
        // console.log("[FilesList] Starting to fetch files...");
        const response = await fetchIngestedFileListing();
        // console.log("[FilesList] API response:", response);
        
        if (response.error) {
          throw new Error(response.message || "Failed to fetch files");
        }
        
        handleResponseData(response);
        setError(null);
        // console.log("[FilesList] Files loaded successfully");
      } catch (err) {
        // console.error("[FilesList] Error loading files:", err);
        // console.error("[FilesList] Error details:", {
        //   message: err.message,
        //   stack: err.stack,
        //   name: err.name
        // });
        setError(`Failed to load files: ${err.message}`);
        setFiles([]);
        setTotalPages(1);
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    };

    // Load files initially with loader
    loadFiles(true);

    // Set up polling every 30 seconds
    const pollingInterval = setInterval(() => {
      loadFiles(false);
    }, 30000);

    // Clean up the interval when the component unmounts
    return () => {
      clearInterval(pollingInterval);
    };
  }, [filesPerPage, handleResponseData]);

  // Effect to handle filtering by search query
  useEffect(() => {
    // console.log("[FilesList] Filtering effect - searchQuery:", searchQuery, "files:", files);
    if (!searchQuery.trim()) {
      setFilteredFiles(files);
      // console.log("[FilesList] No search query, setting filteredFiles to:", files);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = files.filter((file) =>
        file.toLowerCase().includes(query)
      );
      setFilteredFiles(filtered);
      // console.log("[FilesList] Filtered files:", filtered);
    }

    // Reset to first page when search changes
    setPage(1);
  }, [searchQuery, files]);

  // Effect to handle pagination with filtered files
  useEffect(() => {
    // console.log("[FilesList] Pagination effect - page:", page, "filteredFiles:", filteredFiles);
    // Calculate which files to show on current page
    const startIndex = (page - 1) * filesPerPage;
    const endIndex = startIndex + filesPerPage;
    const currentPageFiles = filteredFiles.slice(startIndex, endIndex);

    // Update total pages based on filtered results
    const total = Math.ceil(filteredFiles.length / filesPerPage);
    setTotalPages(total || 1);

    // Update the paginated files
    setPaginatedFiles(currentPageFiles);
    // console.log("[FilesList] Set paginatedFiles to:", currentPageFiles);
  }, [filteredFiles, page, filesPerPage]);

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Manually refresh file list
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await fetchIngestedFileListing();
      if (response.error) {
        throw new Error(response.message || "Failed to fetch files");
      }

      // Handle the new response format
      if (response.data && Array.isArray(response.data)) {
        // Extract filenames from the new response format
        const fileList = response.data.map((item) => item.filename);
        setFiles(fileList);

        // Calculate total pages
        const total = Math.ceil(fileList.length / filesPerPage);
        setTotalPages(total || 1); // Ensure at least 1 page

        // Process the status information to determine overall progress
        const processedData = response.data.map((file) => {
          // Use the existing helper functions to determine status
          const progressStatus = determineFileProgressStatus(file);
          return { ...file, progressStatus };
        });

        setFilesData(processedData);
        
        // Ensure filteredFiles is immediately updated if no search query
        if (!searchQuery.trim()) {
          setFilteredFiles(fileList);
        }
      } else {
        // Fall back to legacy format if available
        const fileList = response.filenames || [];
        setFiles(fileList);
        setTotalPages(Math.ceil(fileList.length / filesPerPage) || 1);
        
        // Ensure filteredFiles is immediately updated if no search query
        if (!searchQuery.trim()) {
          setFilteredFiles(fileList);
        }
      }

      // Show success snackbar
      setSnackbarMessage(
        "File list refreshed successfully. Additionally, auto refresh set to 30 seconds."
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setError(null);
    } catch (err) {
      // console.error("Error refreshing files:", err);
      setError("Failed to refresh files. Please try again later.");

      // Show error snackbar
      setSnackbarMessage("Failed to refresh file list");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setRefreshing(false);
    }
  };

  // Enhanced function to determine file type icon based on file extension
  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();

    switch (extension) {
      case "pdf":
        return <PdfIcon color="error" />;
      case "ppt":
      case "pptx":
        return <SlideshowIcon color="primary" />;
      // case "doc":
      // case "docx":
      //   return <DocIcon color="info" />;
      // case "jpg":
      // case "jpeg":
      // case "png":
      // case "gif":
      // case "svg":
      // case "bmp":
      //   return <ImageIcon color="success" />;
      // case "js":
      // case "jsx":
      // case "ts":
      // case "tsx":
      // case "html":
      // case "css":
      // case "py":
      // case "java":
      // case "c":
      // case "cpp":
      //   return <CodeIcon color="secondary" />;
      // case "json":
      //   return <JsonIcon color="warning" />;
      // case "csv":
      // case "xls":
      // case "xlsx":
      //   return <CsvIcon color="info" />;
      // case "txt":
      // case "md":
      //   return <TextIcon color="action" />;
      // case "mp4":
      // case "avi":
      // case "mov":
      // case "wmv":
      //   return <VideoIcon color="error" />;
      // case "mp3":
      // case "wav":
      // case "ogg":
      //   return <AudioIcon color="primary" />;
      // case "zip":
      // case "rar":
      // case "tar":
      // case "gz":
      //   return <ZipIcon color="action" />;
      default:
        return <GenericFileIcon color="action" />;
    }
  };

  // Check if a file is selected
  const isFileSelected = (fileName) => {
    return selectedFiles.includes(fileName);
  };

  // Helper function to update step status
  const updateStepStatus = (step, status) => {
    setProcessingStatus((prevStatus) => ({
      ...prevStatus,
      [step]: status,
    }));
  };

  // Helper function to mark subsequent steps as failed
  const markSubsequentStepsAsFailed = (steps, fromIndex) => {
    for (let i = fromIndex + 1; i < steps.length; i++) {
      updateStepStatus(steps[i], "failed");
    }
  };

  // Helper function to process individual step
  const processStep = (step, index, steps, shouldFail) => {
    const status = shouldFail ? "failed" : "completed";
    updateStepStatus(step, status);

    // If a step fails, mark subsequent steps as failed
    if (shouldFail) {
      markSubsequentStepsAsFailed(steps, index);
    }
  };

  // Simulate the processing pipeline steps with timeouts
  const simulateProcessingPipeline = () => {
    const steps = [
      "ingestion_pipeline_start",
      "check_file_exists",
      "pdf_to_images",
      "vlm_processing",
      "embedding_generation",
      "save_processed_data",
      "content_extraction",
      "summary_generation",
      "summary_upload_to_delta",
      "metadata_extraction_from_summary",
      "embedding_upload_to_delta",
      "direct_vector_index_upload",
      "verify_index_ingestion",
      "ingestion_pipeline_completed",
    ];

    // Simulate each step with a random delay
    let currentDelay = 0;

    steps.forEach((step, index) => {
      // Random delay between 1-3 seconds for each step
      const stepDelay = Math.floor(Math.random() * 2000) + 1000;
      currentDelay += stepDelay;

      // Randomly fail one step with 10% probability (except the last step)
      const shouldFail = index < steps.length - 1 && Math.random() < 0.1;

      setTimeout(() => {
        processStep(step, index, steps, shouldFail);
      }, currentDelay);
    });
  };

  // Close the processing dialog
  const handleCloseProcessingDialog = () => {
    setProcessingDialogOpen(false);
    setProcessingFile(null);
  };

  // Handle info button click to open the dialog
  const handleInfoClick = async (e, fileName, run_id) => {
    e.stopPropagation();
    setProcessingFile(fileName);
    // Initialize with loading state
    setProcessingStatus({
      ingestion_pipeline_start: "started",
      check_file_exists: "started",
      pdf_to_images: "started",
      vlm_processing: "started",
      embedding_generation: "started",
      save_processed_data: "started",
      content_extraction: "started",
      summary_generation: "started",
      summary_upload_to_delta: "started",
      metadata_extraction_from_summary: "started",
      embedding_upload_to_delta: "started",
      direct_vector_index_upload: "started",
      verify_index_ingestion: "started",
      ingestion_pipeline_completed: "started",
    });
    setProcessingDialogOpen(true);

    try {
      // console.log("Fetching ingestion progress for:", fileName, run_id);
      // Call the API to get actual ingestion progress status
      const response = await getIngestionProgressLog(fileName, run_id);

      // Check if we got a valid response
      if (response?.data && Array.isArray(response.data)) {
        // Pass the raw array data to the component
        setProcessingStatus(response.data);
      } else if (response?.status) {
        // Handle legacy format if present
        setProcessingStatus(response.status);
      } else {
        console.warn("API response did not contain status information");
        // Fall back to simulation if API doesn't return expected data
        simulateProcessingPipeline();
      }
    } catch (error) {
      console.error("Error fetching ingestion progress status:", error);
      // Fall back to simulation if API request fails
      simulateProcessingPipeline();
    }
  };

  // Add new utility function to check if a file processing has timed out
  const hasProcessingTimedOut = (startTime) => {
    if (!startTime) return false;

    try {
      const startDate = new Date(startTime);
      const currentDate = new Date();

      // Calculate difference in minutes
      const diffInMinutes = (currentDate - startDate) / (1000 * 60);

      // Return true if processing has been going on for more than 30 minutes
      return diffInMinutes > 30;
    } catch (e) {
      console.error("Error checking processing timeout:", e);
      return false;
    }
  };

  // Update formatTimestamp to also include elapsed time information and seconds
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";

    try {
      // Create a date object from the timestamp
      const date = new Date(timestamp);

      // Format the date in IST time zone (UTC+5:30) with seconds
      const formattedDate = new Intl.DateTimeFormat("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        // hour: "2-digit",
        // minute: "2-digit",
        // second: "2-digit", // Added seconds to the format
        // hour12: true,
        timeZone: "Asia/Kolkata", // IST time zone
      }).format(date);

      return formattedDate;
    } catch (e) {
      return timestamp; // Return the original timestamp if parsing fails
    }
  };

  // Get status chip based on the file status
  const getStatusChip = (fileDetails) => {
    const status = fileDetails.progressStatus || fileDetails.status;

    switch (status?.toLowerCase()) {
      case "completed":
        return (
          <>
            {/* Rest of existing code */}
            <Tooltip title="Completed">
              <IconButton
                size="small"
                sx={{
                  padding: { xs: "4px", sm: "6px", md: "8px" },
                }}
              >
                <CheckCircleIcon
                  // fontSize="large"
                  label="Completed"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: "20px" }}
                />
              </IconButton>
            </Tooltip>
          </>
        );
      case "in_progress":
        return (
          <>
            <Tooltip title="In Progress">
              <IconButton
                size="small"
                sx={{
                  padding: { xs: "4px", sm: "6px", md: "8px" },
                }}
              >
                <ScheduleIcon
                  fontSize="small"
                  label="Processing"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: "20px" }}
                />
              </IconButton>
            </Tooltip>
          </>
        );
      case "failed":
        return (
          <>
            <Tooltip title="Failed">
              <IconButton
                size="small"
                sx={{
                  padding: { xs: "4px", sm: "6px", md: "8px" },
                }}
              >
                <ErrorIcon
                  fontSize="small"
                  label="Failed"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: "20px" }}
                />
              </IconButton>
            </Tooltip>
          </>
        );
      default:
        return (
          <>
            <Tooltip title="In Progress">
              <IconButton
                size="small"
                sx={{
                  padding: { xs: "4px", sm: "6px", md: "8px" },
                }}
              >
                <ScheduleIcon
                  fontSize="small"
                  label="Unknown"
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ height: "20px" }}
                />
              </IconButton>
            </Tooltip>
          </>
        );
    }
  };

  // Find file details by filename
  const getFileDetails = (filename) => {
    return filesData.find((file) => file.filename === filename) || {};
  };

  // Add new state for summary dialog
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedSummaryFile, setSelectedSummaryFile] = useState(null);

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Add handler for summary button click
  const handleGenerateSummary = (e, fileName) => {
    e.stopPropagation(); // Prevent triggering the list item click
    setSelectedSummaryFile(fileName);
    setSummaryDialogOpen(true);
  };

  // Add handler to close summary dialog
  const handleCloseSummaryDialog = () => {
    setSummaryDialogOpen(false);
    setSelectedSummaryFile(null);
  };

  // Function to return responsive height based on screen size
  const getResponsiveHeight = () => {
    // Get the viewport height
    const vh = window.innerHeight;

    // Adjust height based on viewport size
    if (vh < 600) return "70vh"; // Very small screens
    if (vh < 800) return "75vh"; // Small screens
    if (vh < 1000) return "75vh"; // Medium screens
    return "85vh"; // Large screens
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Handle SharePoint sync trigger
  // const handleSharePointSync = async () => {
  //   setSyncing(true);
  //   try {
  //     let email_id = localStorage.getItem("userEmail").toLowerCase();
  //     const polled_response = await triggerSharePointSync(email_id);
  //     console.log("SharePoint sync response:", polled_response);
  //     setSnackbarMessage("SharePoint sync triggered. Poll the file list to see new files.");
  //     setSnackbarSeverity("success");
  //   } catch (err) {
  //     setSnackbarMessage(`SharePoint sync failed: ${err.message}`);
  //     setSnackbarSeverity("error");
  //   } finally {
  //     setSyncing(false);
  //     setSnackbarOpen(true);
  //   }
  // };

  // Handle delete file — opens confirmation dialog
  const handleDeleteFile = (e, fileName) => {
    e.stopPropagation();
    setFileToDelete(fileName);
    setDeleteConfirmOpen(true);
  };

  // Perform actual deletion after user confirms
  const handleConfirmDelete = async () => {
    const fileName = fileToDelete;
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
    try {
      const response = await deleteDocument(fileName);
      if (response && !response.error) {
        setSnackbarMessage(`"${fileName}" deleted successfully.`);
        setSnackbarSeverity("success");
        // Remove from local state immediately
        setFiles((prev) => prev.filter((f) => f !== fileName));
        setFilesData((prev) => prev.filter((f) => f.filename !== fileName));
      } else {
        throw new Error(response?.message || "Delete failed");
      }
    } catch (err) {
      setSnackbarMessage(`Failed to delete "${fileName}": ${err.message}`);
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  // Check for early return conditions
  if (loading) {
    return <Loader backgroundColor="rgba(0, 0, 0, 0.8)" zIndex={10000} />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2, color: "error.main" }}>
        <Typography variant="h6" color="error">Error Loading Files</Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>{error}</Typography>
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
          Check the browser console for more details.
        </Typography>
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">No Files Available</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          No files have been uploaded yet or files are still processing.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          height: getResponsiveHeight(),
          overflow: "hidden",
          width: "100%",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header with title, search and refresh controls */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          {/* Title takes most of the space */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
              flexGrow: 1, // Allow title to grow
              order: 1,
            }}
          >
            Available Files
          </Typography>

          {/* Control buttons group aligned to the right */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              order: 2,
            }}
          >
            {/* Search toggle and field */}
            <Box
              sx={{
                position: "relative",
                width: searchVisible ? "20%" : "auto",
                minWidth: searchVisible ? "160px" : "auto",
                transition: "all 0.3s ease",
              }}
            >
              {!searchVisible ? (
                <Tooltip title="Search files">
                  <IconButton
                    onClick={toggleSearch}
                    size="small"
                    color="primary"
                    sx={{
                      p: 1,
                      borderRadius: "50%",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                    aria-label="show search"
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <TextField
                  inputRef={searchInputRef}
                  size="small"
                  placeholder="Search files..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  fullWidth
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={
                            searchQuery ? handleClearSearch : toggleSearch
                          }
                          aria-label={
                            searchQuery ? "clear search" : "hide search"
                          }
                        >
                          {searchQuery ? (
                            <ClearIcon fontSize="small" />
                          ) : (
                            <CloseIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 2,
                      backgroundColor: "rgba(0, 0, 0, 0.02)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.1)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                      height: "32px", // Make height consistent
                      fontSize: "0.85rem",
                    },
                  }}
                  sx={{
                    animation: "slideIn 0.3s ease",
                    "@keyframes slideIn": {
                      "0%": { opacity: 0, width: "0%" },
                      "100%": { opacity: 1, width: "100%" },
                    },
                  }}
                />
              )}
            </Box>

            {/* SharePoint Sync button */}
            {/* <Tooltip title="SharePoint Sync">
              <IconButton
                onClick={handleSharePointSync}
                size="small"
                disabled={syncing}
                sx={{
                  color: "primary.main",
                  "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                }}
              >
                {syncing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CloudSyncIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip> */}

            {/* Refresh button */}
            <Tooltip title="Refresh list">
              <IconButton
                onClick={handleRefresh}
                size="small"
                disabled={refreshing}
                sx={{
                  color: "primary.main",
                  "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            {/* Files count - only show when search is not visible */}
            {!searchVisible && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  display: { xs: "none", sm: "block" },
                }}
              >
                {filteredFiles.length} file
                {filteredFiles.length !== 1 ? "s" : ""}{" "}
                {filteredFiles.length !== files.length &&
                  `(filtered from ${files.length})`}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Show filter notice if filtering is active */}
        {searchQuery && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 0.5,
              backgroundColor: "rgba(25, 118, 210, 0.05)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                color: "text.secondary",
                fontSize: "0.8rem",
                flexGrow: 1,
              }}
            >
              Showing {filteredFiles.length} of {files.length} files
              {searchQuery ? ` matching "${searchQuery}"` : ""}
            </Typography>
            {searchQuery && (
              <Button
                size="small"
                variant="text"
                onClick={handleClearSearch}
                startIcon={<ClearIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                Clear filter
              </Button>
            )}
          </Box>
        )}

        {/* Wrap List in a Box with overflow auto */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {filteredFiles.length > 0 ? (
            <List
              dense
              sx={{
                width: "100%",
                flex: 1,
                padding: 0,
                "& .MuiListItem-root": {
                  padding: { xs: "8px 10px", sm: "10px 16px" },
                },
              }}
            >
              {paginatedFiles.map((file, index) => {
                const fileDetails = getFileDetails(file);
                const run_id = fileDetails.run_id || "";
                const isCompleted = fileDetails.progressStatus === "completed";

                return (
                  <React.Fragment key={file}>
                    <ListItem
                      button
                      // onClick={() => handleFileClick(file)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                        },
                        bgcolor: isFileSelected(file)
                          ? "rgba(25, 118, 210, 0.12)"
                          : "inherit",
                        transition: "background-color 0.2s ease",
                        cursor:
                          fileDetails.progressStatus === "completed"
                            ? "pointer"
                            : "default",
                        opacity:
                          fileDetails.progressStatus === "failed" ? 0.7 : 1,
                        display: "flex",
                        flexWrap: { xs: "wrap", sm: "nowrap" }, // Allow wrapping on smaller screens
                        gap: { xs: 1, sm: 0 }, // Add gap on small screens when wrapped
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* File icon and name */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: { xs: "100%", sm: "30%" },
                          minWidth: { xs: "100%", sm: "175px" },
                          mb: { xs: 1, sm: 0 }, // Add margin-bottom on small screens
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: { xs: "24px", sm: "30px", md: "40px" },
                          }}
                        >
                          {getFileIcon(file)}
                        </ListItemIcon>
                        <Typography
                          variant="body2"
                          // onClick={
                          //   isCompleted
                          //     ? () => handleFileClick(file,run_id)
                          //     : (e) => handleInfoClick(e, file, run_id)
                          // }
                          noWrap
                          sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.8rem",
                              md: "0.875rem",
                            },
                            maxWidth: {
                              xs: "calc(100vw - 120px)",
                              sm: "120px",
                              md: "150px",
                              lg: "300px",
                            },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "default",
                          }}
                          title={file}
                        >
                          {file}
                        </Typography>
                      </Box>

                      {/* Date and time (moved to center) */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: {
                            xs: "flex-start",
                            sm: "center",
                          },
                          width: { xs: "50%", sm: "20%" },
                          order: { xs: 3, sm: 2 }, // Reorder elements on mobile
                        }}
                      >
                        {fileDetails.start_time && (
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                hasProcessingTimedOut(fileDetails.start_time) &&
                                fileDetails.progressStatus !== "completed"
                                  ? "error.main"
                                  : "text.secondary",
                              fontSize: {
                                xs: "0.6rem",
                                sm: "0.65rem",
                                md: "0.75rem",
                              },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatTimestamp(fileDetails.start_time)}
                          </Typography>
                        )}
                      </Box>

                      {/* Generate Summary button - only for completed files */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: { xs: "50%", sm: "20%" },
                          order: { xs: 4, sm: 3 },
                        }}
                      >
                        {isCompleted && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={(e) => handleGenerateSummary(e, file)}
                            sx={{
                              textTransform: "none",
                              fontSize: {
                                xs: "0.7rem",
                                sm: "0.75rem",
                                md: "0.8rem",
                              },
                              py: 0.5,
                              px: { xs: 1, sm: 1.5 },
                              fontFamily: "'Poppins', sans-serif",
                              whiteSpace: "nowrap",
                              display: { xs: "none", sm: "flex" },
                            }}
                            startIcon={
                              <InfoIcon
                                sx={{ fontSize: { xs: "0.8rem", sm: "1rem" } }}
                              />
                            }
                          >
                            Generate Summary
                          </Button>
                        )}
                        {/* Mobile version with icon only */}
                        {isCompleted && (
                          <Tooltip title="Generate Summary">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => handleGenerateSummary(e, file)}
                              sx={{
                                display: { xs: "flex", sm: "none" },
                                padding: "4px",
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Status and actions */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: { xs: 0.5, sm: 1 },
                          width: { xs: "50%", sm: "30%" },
                          order: { xs: 2, sm: 4 }, // Reorder elements on mobile
                        }}
                      >
                        {fileDetails.process_steps && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.75rem",
                                display: "block",
                              }}
                            >
                              {fileDetails.process_steps.completed} of{" "}
                              {fileDetails.process_steps.total} steps completed
                              ({fileDetails.process_steps.percent_complete}%)
                            </Typography>
                          </Box>
                        )}
                        {getStatusChip(fileDetails)}
                        <Tooltip title="View Processing Logs">
                          <IconButton
                            size="small"
                            onClick={(e) => handleInfoClick(e, file, run_id)}
                            sx={{
                              padding: { xs: "2px", sm: "4px", md: "6px" },
                            }}
                          >
                            <InfoIcon
                              sx={{
                                fontSize: {
                                  xs: "1rem",
                                  sm: "1.1rem",
                                  md: "1.2rem",
                                },
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete File">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={fileDetails.process_steps?.percent_complete !== 100}
                            onClick={(e) => handleDeleteFile(e, file)}
                            sx={{
                              padding: { xs: "2px", sm: "4px", md: "6px" },
                            }}
                          >
                            <CloseIcon
                              sx={{
                                fontSize: {
                                  xs: "1rem",
                                  sm: "1.1rem",
                                  md: "1.2rem",
                                },
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    {index < paginatedFiles.length - 1 && (
                      <Divider component="li" />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                p: 4,
                color: "text.secondary",
              }}
            >
              <Typography variant="body1" align="center">
                No files found matching the search criteria.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Pagination controls - fixed position at bottom of paper */}
        {/* {totalPages > 1 && ( */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
            display: "flex",
            justifyContent: "space-between", // Changed from flex-end to space-between
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Information text on left side */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon 
              fontSize="small" 
              color="action" 
              sx={{ fontSize: '1rem' }} 
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "0.8rem",
              }}
            >
              *Uploaded files will appear in the list once the file ingestion process begins on Databricks Workflow. 
              {/* Please note, there may be a delay in starting the process due to idle Databricks compute. */}
            </Typography>
          </Box>

          {/* Pagination controls on right side */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "0.875rem",
              }}
            >
              Page {page} of {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "4px",
                },
              }}
            />
          </Box>
        </Box>
        {/* )} */}
      </Paper>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setFileToDelete(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            color: "#1B2559",
            borderBottom: "1px solid #E2E8F0",
            padding: "16px 24px",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1, px: 3 }}>
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", mt: 1 }}>
            Are you sure you want to delete{" "}
            <strong>{fileToDelete}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, py: 2 }}>
          <Button
            onClick={() => { setDeleteConfirmOpen(false); setFileToDelete(null); }}
            sx={{ textTransform: "none", fontFamily: "'Poppins', sans-serif" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontFamily: "'Poppins', sans-serif" }}
          >
            Delete
          </Button>
        </Box>
      </Dialog>

      {/* Processing status dialog - fix duplicated dialog */}
      <Dialog
        open={processingDialogOpen}
        onClose={handleCloseProcessingDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            color: "#1B2559",
            borderBottom: "1px solid #E2E8F0",
            padding: "16px 24px",
          }}
        >
          Processing Status
          <IconButton onClick={handleCloseProcessingDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FileProcessingProgress
            fileName={processingFile}
            pipelineStatus={processingStatus}
            onClose={handleCloseProcessingDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Summary dialog for file summaries */}
      <SummaryDialog
        open={summaryDialogOpen}
        onClose={handleCloseSummaryDialog}
        selectedFile={selectedSummaryFile}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: { xs: '24px', sm: '40px' }, // Adjust bottom margin for different screen sizes
          '& .MuiAlert-root': {
            width: '100%',
            maxWidth: '600px', // Limit maximum width
            mx: 'auto', // Auto margins on left and right
            boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
          }
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          // variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

FilesList.propTypes = {
  onSelectFile: PropTypes.func,
  onDeleteFile: PropTypes.func,
  selectedFiles: PropTypes.array
};

export default FilesList;
